<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex class="d-flex justify-space-between mb-3 align-center" xs12>
        <h3 class="font-weight-bold">Offices</h3>
        <div class="text-xs-right">
          <v-btn
              @click="showOfficeDialog"
              color="blue darken-3"
              dark
              fab
              v-if="$oauth.checkIfHasPermission(['alter office'])"
              x-small>
            <v-icon>add</v-icon>
          </v-btn>
          <export-button
              class="ml-2"
              v-if="$oauth.checkIfHasPermission(['export office'])"/>
          <refresh-button :click-function="refreshData"></refresh-button>
        </div>
      </v-flex>
      <v-flex xs12>
        <v-card>
          <v-layout class="py-1 mx-2" row wrap>
            <v-flex class="pb-0" xs6>
              <v-text-field
                  :elevation="0"
                  class="hidden-sm-and-down "
                  flat
                  hide-details
                  placeholder="Search by typing here..."
                  prepend-inner-icon="search"
                  solo
                  v-model="filters.query"
                  @keypress.enter="handleFilterChange"
              ></v-text-field>
            </v-flex>
            <v-flex xs6>
              <v-autocomplete
                  :items="typeOption"
                  @change="handleFilterChange"
                  chips
                  clearable
                  deletable-chips
                  flat
                  hide-details
                  label="Office Type"
                  multiple
                  item-value="value"
                  item-text="text"
                  prepend-inner-icon="filter_list"
                  solo
                  v-model="filters.office_type"
              ></v-autocomplete>
            </v-flex>
          </v-layout>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-data-table
                :footer-props="footerProps"
                :headers="headers"
                :items="office_data"
                :loading="loading"
                :options.sync="pagination"
                :server-items-length="totalNumber"
                item-key="id"
                show-expand>
              <template class="shadow-none" v-slot:expanded-item="props">
                <td :colspan="props.headers.length" class="pa-0" v-if="props.headers">
                  <v-list class="pa-0">
                    <list-item-detail-horizontal :value="props.item.address"
                                                 key-text="Address:"></list-item-detail-horizontal>
                    <list-item-detail-horizontal :value="props.item.parent_office"
                                                 key-text="Parent Office:"></list-item-detail-horizontal>
                    <list-item-detail-horizontal :value="props.item.bank_name"
                                                 key-text="Bank:"></list-item-detail-horizontal>
                    <list-item-detail-horizontal
                        :value="getAssociatedRoles(props.item.display_roles)"
                        key-text="Associated Roles:"></list-item-detail-horizontal>
                  </v-list>
                </td>
              </template>
              <template v-slot:item.token_system_active="{ item }">
                               <span>
                                   {{ item.token_system_active ? 'Yes' : 'No' }}
                               </span>
              </template>
              <template v-slot:item.visa_print_system_active="{ item }">
                               <span>
                                   {{ item.visa_print_system_active ? 'Yes' : 'No' }}
                               </span>
              </template>
              <template v-slot:item.action="{ item }">
                <v-tooltip top v-if="$oauth.checkIfHasPermission(['alter office'])">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        @click="setupVisaType(item)"
                        class="mr-2"
                        color="primary lighten-1"
                        dark
                        dense
                        fab
                        v-on="on"
                        x-small
                    >
                      <v-icon small>settings</v-icon>
                    </v-btn>
                  </template>
                  <span>Visa Type Setup</span>
                </v-tooltip>
                <v-tooltip top v-if="$oauth.checkIfHasPermission(['alter office'])">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        @click="edit(item)"
                        color="primary"
                        dark
                        dense
                        fab
                        v-on="on"
                        x-small
                    >
                      <v-icon small>edit</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
                <v-tooltip top v-if="$oauth.checkIfHasPermission(['alter office'])">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        @click="confirmDelete(item)"
                        class="ml-2"
                        color="red lighten-1"
                        dark
                        dense
                        fab
                        v-on="on"
                        x-small
                    >
                      <v-icon small>delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog
        persistent
        transition="dialog-bottom-transition"
        v-if="$oauth.checkIfHasPermission(['alter office'])"
        v-model="form"
        width="800"
    >
      <v-card>
        <v-toolbar color="blue darken-3" dark>
          <v-toolbar-title>{{ formData.id ? 'Edit' : 'New' }} Office</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0">
          <v-container grid-list-md>
            <v-layout wrap>
              <form-error-display-handler :errors="errors"></form-error-display-handler>
              <v-flex xs12>
                <v-form class="pa-2" lazy-validation ref="formRef" v-model="valid">
                  <v-layout row wrap>
                    <v-flex md4>
                      <v-autocomplete v-if="formData.type != 0"
                                      :items="typeOption"
                                      :rules="[rules.required]"
                                      @change="changeOfficeType"
                                      label="Type *"
                                      outlined
                                      v-model="formData.type"
                      ></v-autocomplete>
                      <v-text-field v-else outlined readonly :value="'Main Office'"></v-text-field>
                    </v-flex>
                    <v-flex md8>
                      <v-select
                          :items="offices"
                          item-text="name"
                          item-value="id"
                          label="Parent Office"
                          outlined
                          v-model="formData.office"
                      ></v-select>
                    </v-flex>
                    <v-flex xs8>
                      <v-text-field
                          :rules="[rules.required]"
                          label="Name of Office *"
                          outlined
                          type="text"
                          v-model="formData.name">
                      </v-text-field>
                    </v-flex>

                    <v-flex xs4>
                      <v-text-field
                          :rules="[rules.required]"
                          label="Office Code *"
                          outlined
                          type="text"
                          v-model="formData.slug"
                      ></v-text-field>
                    </v-flex>

                    <!--                    <v-flex sm4>-->
                    <!--                      <v-checkbox-->
                    <!--                          v-model="formData.token_system_active"-->
                    <!--                          label="Token system active"-->
                    <!--                      />-->
                    <!--                    </v-flex>-->
                    <!--                    <v-flex sm4>-->
                    <!--                      <v-checkbox-->
                    <!--                          v-model="formData.visa_sticker_system_active"-->
                    <!--                          label="Visa sticker system active"-->
                    <!--                      />-->
                    <!--                    </v-flex>-->
                    <v-flex sm4>
                      <v-checkbox
                          v-model="formData.visa_print_system_active"
                          label="Visa print system active"
                      />
                    </v-flex>
                    <v-flex sm4>
                      <v-checkbox
                          v-model="formData.can_overstay_regulation"
                          label="Allow OverStay Regulation"
                      />
                    </v-flex>
                    <v-flex sm4>
                      <v-checkbox
                          v-model="formData.can_certification"
                          label="Allow Certification"
                      />
                    </v-flex>
                    <v-flex sm4>
                      <v-checkbox
                          v-model="formData.can_visa_transfer"
                          label="Allow Visa Transfer"
                      />
                    </v-flex>
                    <v-flex sm4>
                      <v-checkbox
                          v-model="formData.can_plan_upgrade"
                          label="Allow Visa Plan Upgarde"
                      />
                    </v-flex>
                    <v-flex sm4>
                      <v-checkbox
                          v-model="formData.can_automation"
                          label="Allow Automation"
                      />
                    </v-flex>
                    <v-flex sm4>
                      <v-checkbox
                          v-model="formData.trekking_automation"
                          label="Allow Trekking Automation"
                      />
                    </v-flex>
                    <v-flex sm4 v-if="embassyOffice">
                      <v-checkbox
                          v-model="formData.eta_print_system_active"
                          label="eTA print system active"
                      />
                    </v-flex>
                    <v-flex sm4>
                      <v-checkbox
                          v-model="formData.otp_service"
                          label="OTP Service"
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout class="row" v-if="formData.token_system_active">
                    <v-flex sm6>
                      <v-text-field
                          :rules="[rules.required]"
                          label="Tourist visa max token per day *"
                          outlined
                          type="number"
                          v-model="formData.tourist_token"></v-text-field>
                    </v-flex>
                    <v-flex sm6>
                      <v-text-field
                          :rules="[rules.required]"
                          label="Non Tourist visa max token per day *"
                          outlined
                          type="number"
                          v-model="formData.non_tourist_token"></v-text-field>
                    </v-flex>
                  </v-layout>
                  <v-layout row v-if="!embassyOffice" wrap>
                    <!--                    <v-flex md6 xs6>-->
                    <!--                      <v-autocomplete-->
                    <!--                          :items="states"-->
                    <!--                          :rules="[rules.required]"-->
                    <!--                          @change="getDistricts($event)"-->
                    <!--                          item-text="name"-->
                    <!--                          item-value="id"-->
                    <!--                          label="State *"-->
                    <!--                          outlined-->
                    <!--                          v-model="formData.state_id"-->
                    <!--                      ></v-autocomplete>-->
                    <!--                    </v-flex>-->
                    <!--                    <v-flex md6 xs6>-->
                    <!--                      <v-autocomplete-->
                    <!--                          :items="districts"-->
                    <!--                          :rules="[rules.required]"-->
                    <!--                          @change="getMunicipalities($event)"-->
                    <!--                          item-text="name"-->
                    <!--                          item-value="id"-->
                    <!--                          label="District *"-->
                    <!--                          outlined-->
                    <!--                          v-model="formData.district_id"-->
                    <!--                      ></v-autocomplete>-->
                    <!--                    </v-flex>-->
                    <!--                    <v-flex md5 xs6>-->
                    <!--                      <v-autocomplete-->
                    <!--                          :items="municipalities"-->
                    <!--                          :rules="[rules.required]"-->
                    <!--                          item-text="name"-->
                    <!--                          item-value="id"-->
                    <!--                          label="Municipality *"-->
                    <!--                          outlined-->
                    <!--                          v-model="formData.city_id"-->
                    <!--                      ></v-autocomplete>-->
                    <!--                    </v-flex>-->
                    <!--                    <v-flex md3 xs4>-->
                    <!--                      <v-text-field-->
                    <!--                          :rules="[rules.required]"-->
                    <!--                          label="Ward No *"-->
                    <!--                          outlined-->
                    <!--                          type="text"-->
                    <!--                          v-model="formData.ward_no"-->
                    <!--                      ></v-text-field>-->
                    <!--                    </v-flex>-->
                    <!--                    <v-flex md4 xs12>-->
                    <!--                      <v-text-field-->
                    <!--                          :rules="[rules.required]"-->
                    <!--                          label="Tole *"-->
                    <!--                          outlined-->
                    <!--                          type="text"-->
                    <!--                          v-model="formData.street_address"-->
                    <!--                      ></v-text-field>-->
                    <!--                    </v-flex>-->
                  </v-layout>
                  <v-layout row v-else>
                    <v-flex md6>
                      <v-autocomplete
                          :items="countries"
                          :rules="[rules.required]"
                          item-text="country_name"
                          item-value="id"
                          label="Country *"
                          outlined
                          type="text"
                          v-model="formData.country_id"
                      ></v-autocomplete>
                    </v-flex>

                    <v-flex md6>
                      <v-text-field
                          :rules="[rules.required]"
                          label="Address *"
                          outlined
                          type="text"
                          v-model="formData.address"
                      ></v-text-field>
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex xs4 v-if="!showForSomeOffices">
                      <v-text-field
                          :rules="[rules.required]"
                          label="Rmis Code *"
                          outlined
                          type="number"
                          v-model="formData.rmis_code"
                          onkeydown="return event.keyCode !== 69 && event.keyCode !== 189"
                      />
                    </v-flex>

                    <v-flex xs6 v-if="!showForSomeOffices">
                      <v-autocomplete
                          :items="banks"
                          :rules="[rules.required]"
                          item-text="bank_name"
                          item-value="id"
                          label="Bank *"
                          outlined
                          type="text"
                          v-model="formData.bank_id"
                      >
                      </v-autocomplete>
                    </v-flex>
                    <v-flex xs12>
                      <file-input :image-url="formData.logo"
                                  label="Office logo"
                                  v-model="formData.logo_image"
                                  :valid_file_types="['png', 'jpeg', 'jpg', 'PNG', 'JPEG', 'JPG']"
                      ></file-input>
                    </v-flex>
                    <v-flex xs12>
                      <v-text-field
                          :rules="[rules.required,rules.interpol_alert_number]"
                          label="Interpol Alert Number *"
                          outlined
                          type="text"
                          mask="mask"
                          hint="9999999999,9999999999"
                          v-model="formData.interpol_alert_number"
                      ></v-text-field>
                    </v-flex>
                    <v-flex xs12>
                      <v-text-field
                          :rules="[rules.required,rules.interpol_alert_email]"
                          label="Interpol Alert Email *"
                          outlined
                          type="text"
                          v-model="formData.interpol_alert_email"
                          hint="abc@gmail.com,def@gmail.com"
                      ></v-text-field>
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap>
                    <v-flex xs12>
                      <h4>Email
                        <v-btn @click="addExtraEmail" class="pull-right"
                               color="primary white--text" fab
                               x-small>
                          <v-icon>add</v-icon>
                        </v-btn>
                      </h4>
                    </v-flex>
                  </v-layout>

                  <v-layout
                      row
                      v-for="(val,i) in emails"
                      wrap>


                    <v-flex sm11 transition="scale-transition">
                      <v-text-field
                          :rules="rules.email"
                          label="Office Email *"
                          outlined
                          type="text"
                          v-model="val.email"
                      />
                    </v-flex>

                    <v-flex class="align-center" sm1 transition="scale-transition">
                      <v-btn
                          @click="removeEmail(i)"
                          class="pull-right"
                          color="red darken-3"
                          dark
                          fab
                          x-small>
                        <v-icon dark>remove</v-icon>
                      </v-btn>
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap>
                    <v-flex class="mb-4 mt-2" xs12>
                      <h4>Phone Number
                        <v-btn @click="addExtraPhone" class="pull-right"
                               color="primary white--text" fab
                               x-small>
                          <v-icon>add</v-icon>
                        </v-btn>
                      </h4>
                    </v-flex>
                  </v-layout>

                  <v-layout
                      row
                      v-for="(val,i) in phone_numbers"
                      wrap>


                    <v-flex sm11 transition="scale-transition">
                      <v-text-field
                          :rules="[rules.required]"
                          label="Office Phone Number *"
                          outlined
                          type="number"
                          v-model="val.phone"
                      />
                    </v-flex>

                    <v-flex class="align-center" sm1 transition="scale-transition">
                      <v-btn
                          @click="removePhone(i)"
                          class="pull-right"
                          color="red darken-3"
                          dark
                          fab
                          x-small>
                        <v-icon dark>remove</v-icon>
                      </v-btn>
                    </v-flex>
                  </v-layout>
                  <v-layout>
                    <v-autocomplete
                        :items="roles"
                        chips
                        deletable-chips
                        item-text="display_name"
                        item-value="id"
                        label="Roles for office"
                        multiple
                        outlined
                        prepend-inner-icon="verified_user"
                        v-model="formData.roles"
                    ></v-autocomplete>
                  </v-layout>
                </v-form>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
              @click="close"
              class="white--text"
              color="red lighten-1"
          >Cancel
          </v-btn>
          <v-btn
              :loading="loading"
              @click="saveModel"
              class="white--text"
              color="blue darken-3"
          >Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
        persistent
        transition="dialog-bottom-transition"
        v-if="$oauth.checkIfHasPermission(['alter office']) && selectedOffice"
        v-model="visaSetupDialog"
        width="800"
    >
      <v-card>
        <v-toolbar color="blue darken-3" dark>
          <v-toolbar-title>Visa Type Setup For {{ selectedOffice.name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeVisaSetupDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0">
          <v-container grid-list-md>
            <v-layout wrap>
              <form-error-display-handler :errors="errors"></form-error-display-handler>
              <v-flex xs12>
                <v-form class="pa-2" lazy-validation ref="formRef" v-model="valid">
                  <v-layout row wrap v-for="(item,index) in visa_type_setup_data" :key="item">
                    <v-flex xs1>
                      <h3>{{ index + 1 }}</h3>
                    </v-flex>
                    <v-flex class="py-0" xs9>
                      <v-autocomplete
                          :items="visa_types"
                          class="hidden-sm-and-down ml-1"
                          flat
                          item-text="type"
                          item-value="id"
                          label="Select Visa Type *"
                          outlined
                          :rules="[rules.required]"
                          solo
                          v-model="item.visa_type_id"
                      >
                      </v-autocomplete>
                    </v-flex>
                    <v-flex xs1>
                      <v-btn @click="addVisaSetup" class="pull-right"
                             color="primary white--text" fab
                             x-small>
                        <v-icon>add</v-icon>
                      </v-btn>
                    </v-flex>
                    <v-flex xs1>
                      <v-btn v-if="index > 0"
                             @click="visa_type_setup_data.splice(index,1)"
                             class="pull-right"
                             color="red darken-3"
                             dark
                             fab
                             x-small>
                        <v-icon dark>remove</v-icon>
                      </v-btn>
                    </v-flex>
                    <v-flex sm4>
                      <v-checkbox
                          v-model="item.can_extend"
                          label="Allow Extension"
                      />
                    </v-flex>
                    <v-flex sm4>
                      <v-checkbox
                          v-model="item.can_convert"
                          label="Allow Conversion"
                      />
                    </v-flex>
                    <v-flex class="py-0" xs12 v-if="item.can_convert">
                      <v-autocomplete
                          :items="visa_types"
                          chips
                          class="hidden-sm-and-down ml-1"
                          deletable-chips
                          flat
                          item-text="type"
                          item-value="slug"
                          label="Can Covert To *"
                          multiple
                          :rules="[rules.required]"
                          outlined
                          solo
                          v-model="item.can_convert_to"
                      >
                      </v-autocomplete>
                    </v-flex>
                  </v-layout>
                </v-form>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
              @click="closeVisaSetupDialog"
              class="white--text"
              color="red lighten-1"
          >Cancel
          </v-btn>
          <v-btn
              :loading="loading"
              @click="saveVisaSetupModel"
              class="white--text"
              color="blue darken-3"
          >Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import Http from "axios";
import {API_URL, VERSION} from "../config/config.js";
import {mapActions, mapGetters} from "vuex";
import {OfficeTypeConstant} from "../constants/AppConstant";
import {commonListMixin} from "../mixins/commonListMixin";
import {validationMixin} from "../mixins/validationMixin";
import TableStatus from "../components/TableStatus";
import FormErrorDisplayHandler from "../components/FormErrorDisplayHandler";
import ListItemDetailHorizontal from "../components/ListItemDetailHorizontal";
import FileInput from "../components/FileInput";
import RefreshButton from "@/components/RefreshButton";
import axios from "axios";

let officeTypes = OfficeTypeConstant.filter((o) => o.text !== 'Main Office');

export default {
  components: {RefreshButton, FileInput, FormErrorDisplayHandler, ListItemDetailHorizontal, TableStatus},
  data() {
    return {
      states: [],
      districts: [],
      municipalities: [],
      banks: [],
      embassyOffice: false,
      governmentInstitute: false,
      statusOptions: [
        {
          text: "Inactive",
          value: "0"
        },
        {
          text: "Active",
          value: "1"
        }
      ],
      typeOption: officeTypes,
      headers: [
        {
          text: 'S.N.',
          value: 's_no',
          sortable: false
        },
        {
          text: "Name",
          value: "name"
        },
        // {
        //   text: "Token system active",
        //   value: "token_system_active"
        // },
        {
          text: "Visa print system active",
          value: "visa_print_system_active"
        },
        {
          text: "Office code",
          value: "slug"
        },
        {
          text: "Type",
          value: "type_name"
        },
        {
          text: "RMIS Code",
          value: "rmis_code"
        },
        {
          text: "Total user",
          value: "total_user"
        }
      ],
      emails: [],
      phone_numbers: [],
      offices: [],
      embassyOffices: [],
      permanentMissionOffices: [],
      mainOffices: [],
      selectedOffice: null,
      visaSetupDialog: false,
      visa_type_setup_data: [{
        visa_type_id: null,
        can_extend: 0,
        can_convert: 0,
        can_convert_to: []
      }]
    };
  },

  computed: {
    ...mapGetters({
      countries: 'CountryList/all',
      roles: 'RoleList/all',
      visa_types: 'VisaTypeList/all'
    }),
    office_data() {
      return this.$store.state.CommonList.all;
    },
    showForSomeOffices() {
      return this.embassyOffice || this.governmentInstitute;
    },
  },
  mixins: [validationMixin, commonListMixin],
  methods: {
    ...mapActions({
      fetchCountries: 'CountryList/fetchCountries',
      fetchOffices: 'OfficeList/fetchData',
      fetchRoles: 'RoleList/fetchData',
      fetchVisaTypes: 'VisaTypeList/fetchAll'
    }),
    showOfficeDialog() {
      this.embassyOffice = false;
      this.offices = this.mainOffices;
      this.showAddDialog();
    },
    saveModel() {
      if (this.$refs.formRef.validate()) {
        let formData = new FormData();
        formData.set("id", this.formData.id);
        formData.set("name", this.formData.name);
        if (this.formData.rmis_code && !this.showForSomeOffices)
          formData.set("rmis_code", this.formData.rmis_code);
        formData.set("type", this.formData.type);
        if (this.formData.office) {
          formData.set("office", this.formData.office);
        }
        if (this.formData.token_system_active) {
          formData.set("token_system_active", this.formData.token_system_active);
          formData.set("tourist_token", this.formData.tourist_token);
          formData.set("non_tourist_token", this.formData.non_tourist_token);
        }
        if (this.formData.visa_sticker_system_active)
          formData.set("visa_sticker_system_active", this.formData.visa_sticker_system_active);
        if (this.formData.visa_print_system_active)
          formData.set("visa_print_system_active", this.formData.visa_print_system_active);
        if (this.formData.eta_print_system_active)
          formData.set("eta_print_system_active", this.formData.eta_print_system_active);
        if (this.formData.trekking_automation)
          formData.set("trekking_automation", this.formData.trekking_automation);
        if (!this.embassyOffice && this.formData.state_id) {
          formData.set("state_id", this.formData.state_id);
          formData.set("district_id", this.formData.district_id);
          formData.set("city_id", this.formData.city_id);
          formData.set("ward_no", this.formData.ward_no);
          formData.set("street_address", this.formData.street_address);
        }
        if (this.formData.country_id)
          formData.set("country_id", this.formData.country_id);
        formData.set("slug", this.formData.slug);
        if (this.formData.bank_id)
          formData.set("bank_id", this.formData.bank_id);

        if (this.emails)
          for (let i = 0; i < this.emails.length; i++) {
            formData.set(`email[${i}]`, this.emails[i].email);
          }
        if (this.phone_numbers)
          for (let i = 0; i < this.phone_numbers.length; i++) {
            formData.set(`phone[${i}]`, this.phone_numbers[i].phone);
          }
        if (this.formData.roles)
          for (let i = 0; i < this.formData.roles.length; i++) {
            formData.set(`roles[${i}]`, this.formData.roles[i]);
          }
        if (this.formData.logo_image)
          formData.set("logo", this.formData.logo_image);

        formData.set('interpol_alert_number', this.formData.interpol_alert_number)
        formData.set('interpol_alert_email', this.formData.interpol_alert_email)
        formData.set('can_overstay_regulation', this.formData.can_overstay_regulation)
        formData.set('can_certification', this.formData.can_certification)
        formData.set('can_visa_transfer', this.formData.can_visa_transfer)
        formData.set('can_plan_upgrade', this.formData.can_plan_upgrade)
        formData.set('can_automation', this.formData.can_automation)
        formData.set('otp_service', this.formData.otp_service ? 1 : 0)
        this.modelData = formData;
        this.save('Office').then(() => {
          this.fetchOffices();
          this.emails = [];
          this.phone_numbers = [];
        });
      }
    },
    saveVisaSetupModel() {
      if (this.$refs.formRef.validate()) {
        axios.post(API_URL + VERSION + `/offices/${this.selectedOffice.id}/visa-type-setup`, {visa_type_setup_data: this.visa_type_setup_data}).then(r => {
          this.$store.commit("Notification/showSuccess", 'Office visa type setup successfully');
          this.visaSetupDialog = false;
        }).catch(e => {
          this.errors = e.response.data.data;
        })
      }
    },
    getBanks() {
      Http.get(API_URL + VERSION + "/banks")
          .then(response => {
            this.banks = response.data.data;
          })
    },
    getStates() {
      Http.get(API_URL + VERSION + "/states/fetch-all")
          .then(response => {
            this.states = response.data.data;
          })

    },
    getDistricts(stateId) {
      Http.get(API_URL + VERSION + "/district/" + stateId)
          .then(response => {
            this.districts = response.data.data;
            if (this.formData.city_id) {
              this.getMunicipalities(this.formData.district_id);
            }
          })
    },
    getMunicipalities(districtId) {
      Http.get(API_URL + VERSION + "/municipality/" + districtId)
          .then(response => {
            this.municipalities = response.data.data;
          })
    },
    changeOfficeType: function () {
      this.governmentInstitute = false;
      let officeType = OfficeTypeConstant.find((type) => type.value === this.formData.type);

      if (officeType && (officeType.text.toLowerCase() === 'embassy' ||
          officeType.text.toLowerCase() === 'consulate general' ||
          officeType.text.toLowerCase() === 'honorary consuls general' ||
          officeType.text.toLowerCase() === 'permanent mission'
      )) {
        this.embassyOffice = true;
        this.formData.city_id = null;
        this.formData.district_id = null;
        this.formData.ward_no = null;
        if (this.formData.type === 4) {
          this.offices = this.embassyOffices
        } else if (this.formData.type === 8) {
          this.offices = [...this.embassyOffices, ...this.permanentMissionOffices]
        } else {
          this.offices = this.mainOffices
        }
      } else {

        if (officeType && officeType.text.toLowerCase() === 'government institution') {
          this.governmentInstitute = true;
        }
        this.offices = this.mainOffices
        this.embassyOffice = false;
        this.formData.country_id = null;
      }

    },
    edit(item) {
      this.governmentInstitute = false;
      this.formData = JSON.parse(JSON.stringify(item));
      this.formData.office = item.main_office;
      this.formData.bank_id = item.bank_id;
      let embassyOffice = false;
      if (parseInt(item.type) === 4 || parseInt(item.type) === 6 || parseInt(item.type) === 7 || parseInt(item.type) === 8)
        embassyOffice = true;

      if (parseInt(item.type) === 6)
        this.governmentInstitute = true;

      this.embassyOffice = embassyOffice;
      this.formData.type = parseInt(this.formData.type);
      if (!this.embassyOffice)
        this.getDistricts(this.formData.state_id);

      if (this.formData.type === 8) {
        this.offices = [...this.embassyOffices, ...this.permanentMissionOffices]
      } else {
        this.offices = this.mainOffices
      }
      if (item.email) {
        this.emails = item.email.map((i) => {
          return {
            "email": i
          }
        });
      }
      if (item.phone) {
        this.phone_numbers = item.phone.map((i) => {
          return {
            "phone": i
          }
        });
      }
      this.form = true;
    },
    async confirmDelete(item) {
      if (item.total_user > 0)
        this.$store.commit('Notification/showError', 'You cannot delete this office. Active user exists in this office.');
      else
        this.destroy(item, 'Office deleted successfully')
    },
    getAssociatedRoles(items) {
      let collection = '';
      if (items !== undefined) {
        for (let i = 0; i < items.length; i++) {
          collection += items[i].name;
          if (i < items.length - 1) {
            collection += ", "
          }
        }
      }
      return collection;
    },
    addExtraEmail() {
      this.emails.push({
        email: ""
      });
    },
    removeEmail(index) {
      this.emails.splice(index, 1);
    },
    addExtraPhone() {
      this.phone_numbers.push({
        phone: ""
      });
    },
    removePhone(index) {
      this.phone_numbers.splice(index, 1);
    },
    close() {
      this.form = false;
      this.errors = [];
      this.emails = [];
      this.phone_numbers = [];
      this.formData = {};
    },
    setParentOffice() {

      this.$store.commit('OfficeList/setFilter', {main_office: true});
      if (this.$store.state.OfficeList.offices.length === 0) {
      }
      this.fetchOffices(true).then(res => this.mainOffices = res);

      this.$store.commit('OfficeList/setFilter', {type: 4});

      this.fetchOffices().then(res => this.embassyOffices = res);

      this.$store.commit('OfficeList/setFilter', {type: 7});

      this.fetchOffices().then(res => this.permanentMissionOffices = res);
    },
    refreshData() {
      this.getAll();
    },
    addVisaSetup() {
      this.visa_type_setup_data.push({
        visa_type_id: null,
        can_extend: 0,
        can_convert: 0,
        can_convert_to: []
      })
    },
    setupVisaType(item) {
      if (item && item.visa_types.length > 0) {
        this.visa_type_setup_data = item.visa_types.map(i => {
          console.log(i)
          return {
            visa_type_id: i.pivot.visa_type_id,
            can_convert: i.pivot.can_convert,
            can_extend: i.pivot.can_extend,
            can_convert_to: JSON.parse(i.pivot.can_convert_to)
          }
        })
      }
      this.selectedOffice = item
      this.visaSetupDialog = true;
    },
    closeVisaSetupDialog() {
      this.selectedOffice = null;
      this.visaSetupDialog = false;
      this.visa_type_setup_data = [{
        visa_type_id: null,
        can_convert: 0,
        can_extend: 0,
        can_convert_to: []
      }]
    }
  },
  created() {
    if (this.$oauth.checkIfHasPermission(['alter office']))
      this.headers.push(
          {
            text: "Action",
            value: "action",
            sortable: false
          })
  },
  mounted() {
    this.$store.commit("CommonList/setModel", "offices");
    this.getStates();
    this.getBanks();
    if (this.$store.state.CountryList.all.length === 0) {
      this.fetchCountries();
    }
    if (this.$store.state.RoleList.all.length === 0) {
      this.fetchRoles();
    }
    this.fetchVisaTypes()
    this.setParentOffice();
  }
};
</script>
